<template>
  <!-- SOLO APPLICATIONS -->
  <template v-if="soloApp">
    <div class="caption">
      <p>Your application must be submitted before the expiration date above to be considered.</p>
      <p v-if="showReturnLink">
        Click
        <a class="inline-link" @click="redirectToBorrowerApp">here</a>
        to review your application.
      </p>
    </div>
  </template>
  <!-- JOINT APPLICATIONS -->
  <template v-else-if="!soloApp">
    <div v-if="showResendInviteLink === true" class="caption">
      <p>
        We are currently waiting for your invited applicant to complete their portion of the
        application. They must submit their portion before the expiration date above or the
        application will be cancelled.
      </p>
      <div class="invite">
        <a @click="resendInvite"
          >Do you want to resend the invite?<i
            class="icon-ok-circle"
            :class="[inviteResent ? 'complete' : 'incomplete']"
          ></i
        ></a>
        <span class="alert" v-if="inviteResent">&#10240;Invite resent!</span>
        <span class="italic" v-else>&#10240;&#60;&#60;&#10240;Click to resend.</span>
      </div>
    </div>
    <div v-else-if="showRestartLink === true" class="caption">
      <p>
        Unfortunately, the second applicant on your application did not register, so you will need
        to restart the application process.
      </p>
      <p>
        Follow this
        <a class="inline-link" :href="restartLink">link</a>
        to begin again.
      </p>
    </div>
    <div v-else-if="showReturnLink === true" class="caption">
      <p>Your application must be submitted before the expiration date above to be considered.</p>
      <p v-if="showReturnLink">
        Click
        <a class="inline-link" @click="redirectToBorrowerApp">here</a>
        to review your application.
      </p>
    </div>
    <div v-else-if="secondaryApplicantIsRegistered === true" class="caption">
      <p>
        We're just waiting on your
        {{ invitedApplicantTypeDisplay }}
        to finish their part of the application. Have them log into their applicant portal and click
        the link to continue.
      </p>
    </div>
  </template>
  <!-- DEFAULT -->
  <template v-else>
    <div class="caption">
      <p>Your application must be submitted before the expiration date above to be considered.</p>
    </div>
  </template>
  <alert-modal
    v-if="resendInviteError === true"
    @close="
      {
        resendInviteError = false;
      }
    "
    :alertText="'We\'re sorry. We cannot complete your request at this time. Please try again later.'"
  ></alert-modal>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import { $api } from "@/services/api";
import { InviteRequest } from "@/models/api";
import { Applicant } from "@/models/application";
import AlertModal from "@/components/modals/AlertModal.vue";
import { ApplicantTypeIds } from "@/config/constants";

const store = useStore();

const applicationId = computed(() => store?.state?.appData?.application?.id);
const currentApplicant = computed(() => store?.state?.appData?.currentApplicant);

const restartLink = computed<string>(
  () =>
    store?.state?.configuration?.borrowerApplicationUrl,
);

// The configured link carries the lender/program query; the return endpoints hang off its host and path.
const borrowerAppBaseUrl = computed<string>(() => {
  const url = new URL(store.state.configuration.borrowerApplicationUrl);
  return `${url.origin}${url.pathname}`.replace(/\/$/, "");
});

const primaryApplicantTypeDisplayName = computed<string>(
  () => store.getters["primaryApplicantTypeDisplayName"],
);

const coApplicantTypeDisplayName = computed<string>(
  () => store.getters["coApplicantTypeDisplayName"],
);

const invitedApplicantTypeDisplay = computed(() => {
  return secondaryApplicantInfo.value?.applicantTypeId === ApplicantTypeIds.PRIMARY_APPLICANT ||
    invitedApplicantInfo.value?.applicantTypeId === ApplicantTypeIds.PRIMARY_APPLICANT
    ? primaryApplicantTypeDisplayName.value?.toLowerCase()
    : coApplicantTypeDisplayName.value?.toLowerCase();
});

const invitedApplicantInfo = computed<Applicant>(() => {
  if ((store?.state?.appData?.applicants || []).length > 1) {
    return store?.state?.appData?.applicants.find(
      (a: Applicant) => a.applicantTypeId !== currentApplicant.value?.applicantTypeId,
    );
  } else {
    return {} as Applicant;
  }
});

const soloApp = computed<boolean>(
  () => store?.state?.appData?.application?.metadata?.withCoApplicants === false,
);

const gettingData = ref<boolean>(true);
const isInvitedApplicant = ref<boolean | undefined>(undefined);
const isSecondaryApplicant = ref<boolean | undefined>(undefined);
const invite = ref({} as any);
const inviteAccepted = ref<boolean | undefined>(undefined);
const inviteResent = ref<boolean | undefined>(undefined);
const resendInviteError = ref<boolean>(false);
const secondaryApplicantIsRegistered = ref<boolean | undefined>(undefined);
const secondaryApplicantInfo = ref({} as Applicant);
const showRestartLink = ref<boolean | undefined>(undefined);
const showResendInviteLink = ref<boolean | undefined>(undefined);
const showReturnLink = ref<boolean | undefined>(undefined);

async function redirectToBorrowerApp() {
  let link = await $api.return.getReturnLink(
    borrowerAppBaseUrl.value,
    store.state.appData.application.id,
  );

  location.href = `${borrowerAppBaseUrl.value}/return?applicationId=${store.state.appData.application.id}&token=${link?.data?.token}`;
}

async function resendInvite() {
  const appId = applicationId.value;
  if (inviteResent.value === undefined || inviteResent.value === false) {

    let inviteRequest = {
      applicationId: appId,
      invitedByApplicantId: currentApplicant.value?.id,
      invitedApplicantTypeId: store.state.invites[0]?.applicantTypeId,
      firstName: store.state.invites[0]?.firstName,
      middleName: store.state.invites[0]?.middleName,
      lastName: store.state.invites[0]?.lastName,
      emailAddress: store.state.invites[0]?.emailAddress,
    } as InviteRequest;
    // Get current invite and use the response to build the request body
    let response = await $api.applications.resendInvite(appId, inviteRequest);
    if (response?.err || response?.error || response?.invites[0].resendAtUtc === null) {
      resendInviteError.value = true;
    } else {
      inviteResent.value = true;
    }
  }
}

async function getApplicationStatusInfo() {
  gettingData.value = true;
  // Solo app
  if (soloApp.value) {
    showReturnLink.value =
      currentApplicant.value?.submittedAt === null ||
      currentApplicant.value?.submittedAt === undefined;
  }
  // Joint app
  if (!soloApp.value) {
    let res = (await $api.applications.getApplicationInvites(applicationId.value)) || [];

    if (res?.error || res?.length <= 0) {
      /************** NORMAL FLOW CHECK isPresent = true **************/
      showResendInviteLink.value = false;

      // primary applicant is logged in and left off on isPresent step show return link
      if (store?.state?.appData?.applicants?.length <= 1) {
        isSecondaryApplicant.value = false;
        showReturnLink.value = true;
      }
      // Secondary applicant exists
      if (store?.state?.appData?.applicants?.length > 1) {
        secondaryApplicantInfo.value = store?.state?.appData?.applicants?.pop();
        isSecondaryApplicant.value =
          currentApplicant.value?.applicantTypeId === secondaryApplicantInfo.value?.applicantTypeId;

        // Secondary applicant is logged in and has not submitted their portion
        if (isSecondaryApplicant.value === true && currentApplicant.value?.submittedAt === null) {
          showReturnLink.value = true;
        }

        // Primary applicant is logged in
        if (isSecondaryApplicant.value === false) {
          // scenario: Primary applicant has NOT submitted their portion yet.
          if (currentApplicant.value?.submittedAt === null) {
            showReturnLink.value = true;
            showRestartLink.value = false;
          }
          // scenario: secondary applicant is registered
          if (secondaryApplicantInfo.value?.userId !== null) {
            showRestartLink.value = false;
            showReturnLink.value = false;
            secondaryApplicantIsRegistered.value = true;
          }
          // scenario: secondary applicant abandoned application before registering
          if (
            currentApplicant.value?.submittedAt !== null &&
            secondaryApplicantInfo.value?.userId === null
          ) {
            showRestartLink.value = true;
            showReturnLink.value = false;
            secondaryApplicantIsRegistered.value = false;
          }
        }
      }
    } else {
      /************** INVITE FLOW CHECK isPresent = false **************/
      let invites = res.invites;
      if ((invites || []).length > 0) {
        let invitedApplicant = invites[0];
        isInvitedApplicant.value =
          invitedApplicant.applicantTypeId === currentApplicant.value?.applicantTypeId;
        inviteAccepted.value = invitedApplicant?.accepted;
        invite.value = invites[0];
        store.dispatch("setInvites", invites);

        // Check if invite has been resent
        if (invitedApplicant.resendAtUtc !== null) {
          inviteResent.value = true;
        } else {
          inviteResent.value = false;
        }
        // Secondary applicant is logged in and has not submitted their portion
        if (
          isInvitedApplicant.value === true &&
          (store?.state?.appData?.currentApplicant?.submittedAt === null ||
            store?.state?.appData?.currentApplicant?.submittedAt === undefined)
        ) {
          showReturnLink.value = true;
        }

        // Primary applicant is logged in
        if (isInvitedApplicant.value === false) {
          showResendInviteLink.value = true;
          showReturnLink.value = undefined;

          // 1. Invite not accepted yet
          if (invitedApplicant?.accepted === false) {
            showResendInviteLink.value = true;
          }

          // 2. Invite accepted, but no username created
          if (
            invitedApplicant?.accepted === true &&
            (invitedApplicantInfo.value?.userId === null ||
              invitedApplicantInfo.value?.userId === undefined)
          ) {
            showResendInviteLink.value = false;
            showRestartLink.value = true;
          }

          // 3. Invite accepted, secondary applicant IS registered
          if (invitedApplicant?.accepted === true && invitedApplicantInfo.value?.userId !== null) {
            showResendInviteLink.value = false;
            showRestartLink.value = false;
            secondaryApplicantIsRegistered.value = true;
          }
        }
      }
    }
  }

  gettingData.value = false;
}

onMounted(async () => {
  await getApplicationStatusInfo();
});
</script>
<style scoped lang="scss">
.inline-link {
  text-decoration: underline;
  cursor: pointer;
}
.invite {
  font-size: 1em;
  display: inline-flex;
  flex-direction: row;
  font-weight: 700px;
  width: 100%;
}
.invite a:hover {
  text-decoration: underline;
}
.invite .icon-ok-circle {
  padding-left: 5px;
}

.invite .icon-ok-circle.incomplete {
  color: gray;
}

.invite .icon-ok-circle.complete {
  color: #48a842;
}

.invite span {
  padding-left: 0;
}

.red {
  color: red;
}

span.alert {
  margin: 0;
  padding: 0;
  color: red;
}
@media (max-width: 991px) {
  .invite {
    font-size: 1em;
  }
}
</style>
